import { isNumber } from 'lodash';

/**
 * Format percent value with adaptive decimal places
 * Used for form inputs and direct value formatting
 */
export function formatPercent(value: number, options: { showSign?: boolean; decimals?: 'auto' | 'test' } = {}): string {
  const { showSign = true, decimals = 'auto' } = options;
  const originalValue = +value;
  let formattedValue = showSign ? '-' : '0';

  if (!originalValue || originalValue === 0 || originalValue === null) {
    return formattedValue;
  }

  let fractionDigits: number;
  if (decimals === 'test') {
    // Test format: 4 decimals for < 10, 3 for 10-100, 2 for >= 100
    fractionDigits = originalValue < 10 ? 4 : originalValue < 100 ? 3 : 2;
  } else {
    // Auto format: 3 decimals for < 10, 2 for 10-100, 1 for >= 100
    fractionDigits = originalValue < 10 ? 3 : originalValue < 100 ? 2 : 1;
  }

  formattedValue = originalValue.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return showSign ? `${formattedValue} %` : formattedValue;
}

/**
 * Format percent value for AG Grid
 * 
 * @param paramsOrValue - AG Grid params object with .value or direct value
 * @param options - Formatting options
 * @param options.multiplyBy100 - Multiply value by 100 before formatting (default: false)
 * @param options.decimals - Decimal places: 'auto' (adaptive), 'fixed4' (4 decimals), or number (default: 'auto')
 * @param options.checkAlreadyFormatted - Skip if value already contains '%' (default: true)
 */
export function formatPercentValue(
  paramsOrValue: any,
  options: {
    multiplyBy100?: boolean;
    decimals?: 'auto' | 'fixed4' | number;
    checkAlreadyFormatted?: boolean;
  } = {}
): string {
  const { multiplyBy100 = false, decimals = 'auto', checkAlreadyFormatted = true } = options;
  
  const originalValue = paramsOrValue?.value !== undefined ? paramsOrValue.value : paramsOrValue;
  
  // Handle invalid values
  if (!originalValue || originalValue === '-') {
    return '-';
  }

  // Check if already formatted
  if (checkAlreadyFormatted && originalValue?.toString().indexOf('%') !== -1 && originalValue.toString().indexOf('-') !== 0) {
    return originalValue;
  }

  // Multiply by 100 if needed
  const valueToFormat = multiplyBy100 ? +originalValue * 100 : +originalValue;
  
  if (!isNumber(valueToFormat) || isNaN(valueToFormat)) {
    return '-';
  }

  // Determine decimal places
  let fractionDigits: number;
  if (decimals === 'fixed4') {
    fractionDigits = 4;
  } else if (typeof decimals === 'number') {
    fractionDigits = decimals;
  } else {
    // Auto: adaptive based on value
    if (valueToFormat < 10) {
      fractionDigits = 3;
    } else if (valueToFormat >= 10 && valueToFormat < 100) {
      fractionDigits = 2;
    } else {
      fractionDigits = 1;
    }
  }

  return (
    valueToFormat.toLocaleString('en-US', {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    }) + '%'
  );
}
